import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SettingsIcon, HomeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
// Import all the screens we just built
import { VenueWelcome } from '../components/play/VenueWelcome';
import { GameModeSelector } from '../components/play/GameModeSelector';
import { PlayerEntry } from '../components/play/PlayerEntry';
import { Scorekeeper } from '../components/play/Scorekeeper';
import { WinnerScreen } from '../components/play/WinnerScreen';
import { ShareCard } from '../components/play/ShareCard';
import { Leaderboard } from '../components/play/Leaderboard';
import { AdminDashboard } from '../components/play/AdminDashboard';
type Screen = 'welcome' | 'gameMode' | 'players' | 'scorekeeper' | 'winner' | 'share' | 'leaderboard' | 'admin';
export function PlayApp() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('welcome');
  const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
  // App State
  const [gameMode, setGameMode] = useState<string>('');
  const [players, setPlayers] = useState<string[]>([]);
  const [scores, setScores] = useState<Record<string, number[]>>({});
  const navigateTo = (screen: Screen, dir: number = 1) => {
    setDirection(dir);
    setCurrentScreen(screen);
  };
  // Screen variants for smooth sliding transitions
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.95
    })
  };
  return <div className="min-h-screen bg-brand-charcoal flex items-center justify-center p-0 md:p-8 font-body" data-id="element-880">
      {/* Desktop Phone Frame Wrapper */}
      <div className="w-full h-[100dvh] md:h-[780px] md:max-h-[82vh] md:w-[390px] bg-black md:rounded-[3rem] md:border-[12px] border-black overflow-hidden relative shadow-2xl flex flex-col" data-id="element-881">
        {/* iOS style status bar placeholder (desktop only) */}
        <div className="hidden md:flex h-7 w-full bg-transparent absolute top-0 z-50 justify-center" data-id="element-882">
          <div className="w-32 h-6 bg-black rounded-b-3xl" data-id="element-883" /> {/* Notch */}
        </div>

        {/* Global App Header (hidden on welcome and admin screens) */}
        {currentScreen !== 'welcome' && currentScreen !== 'admin' && currentScreen !== 'share' && <div className="absolute top-0 inset-x-0 h-16 z-40 flex items-center justify-between px-6 bg-gradient-to-b from-brand-charcoal/80 to-transparent pt-2 md:pt-6" data-id="element-884">
              <button onClick={() => navigateTo('welcome', -1)} className="text-brand-cream/50 hover:text-brand-cream-light transition-colors" data-id="element-885">
                <HomeIcon className="w-5 h-5" data-id="element-886" />
              </button>
              <span className="font-heading text-brand-cream-light text-lg tracking-wide" data-id="element-887">
                PuttLab
              </span>
              <button onClick={() => navigateTo('admin', 1)} className="text-brand-cream/30 hover:text-brand-cream-light transition-colors" data-id="element-888">
                <SettingsIcon className="w-5 h-5" data-id="element-889" />
              </button>
            </div>}

        {/* Screen Content Area */}
        <div className="flex-1 relative overflow-hidden bg-brand-charcoal" data-id="element-890">
          <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-891">
            <motion.div key={currentScreen} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="absolute inset-0 w-full h-full" data-id="element-892">
              {currentScreen === 'welcome' && <VenueWelcome onStart={() => navigateTo('gameMode', 1)} onViewLeaderboard={() => navigateTo('leaderboard', 1)} data-id="element-893" />}

              {currentScreen === 'gameMode' && <GameModeSelector onSelect={mode => {
              setGameMode(mode);
              navigateTo('players', 1);
            }} data-id="element-894" />}

              {currentScreen === 'players' && <PlayerEntry gameMode={gameMode} onComplete={p => {
              setPlayers(p);
              navigateTo('scorekeeper', 1);
            }} data-id="element-895" />}

              {currentScreen === 'scorekeeper' && <Scorekeeper gameMode={gameMode} players={players} onFinish={s => {
              setScores(s);
              navigateTo('winner', 1);
            }} data-id="element-896" />}

              {currentScreen === 'winner' && <WinnerScreen gameMode={gameMode} players={players} scores={scores} onShare={() => navigateTo('share', 1)} onLeaderboard={() => navigateTo('leaderboard', 1)} data-id="element-897" />}

              {currentScreen === 'share' && <ShareCard gameMode={gameMode} players={players} scores={scores} onClose={() => navigateTo('winner', -1)} data-id="element-898" />}

              {currentScreen === 'leaderboard' && <Leaderboard onBack={() => navigateTo(scores && players.length > 0 ? 'winner' : 'welcome', -1)} currentPlayers={players.length > 0 ? players : undefined} currentScores={Object.keys(scores).length > 0 ? scores : undefined} data-id="element-899" />}

              {currentScreen === 'admin' && <AdminDashboard onBack={() => navigateTo('welcome', -1)} data-id="element-900" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop context hint — positioned well below the phone frame */}
      <div className="hidden md:flex flex-col items-center fixed bottom-4 left-0 right-0 text-brand-cream/30 text-sm font-body text-center pointer-events-none" data-id="element-901">
        <p className="mb-1" data-id="element-902">Interactive Prototype</p>
        <Link to="/play" className="text-brand-gold hover:underline pointer-events-auto" data-id="element-903">
          Return to main site
        </Link>
      </div>
    </div>;
}